import { DynamicParameterValue, Insight } from "@/types/insights"
import { Clause } from "@/types/view"
import { DateTime, Settings } from "luxon"

/**
 * Changes dateTime to the START of the day UTC
 * Examples:
 * - E.g 2022-09-03T05:00:00Z will become 2022-09-03T00:00:00Z
 * - E.g 2022-09-03T17:00:00-08:00 will become 2022-09-04T00:00:00Z
 * @param {Date | string} date The DateTime object or string to convert.
 * @returns {Date} The Date representing the start of the day in UTC
 */
export const asUtc: (date: Date | string) => Date = (date) => {
	let dateTime: DateTime | null = null
	if (date instanceof Date) {
		dateTime = (DateTime as any).fromObject({
			year: date.getFullYear(),
			month: date.getMonth() + 1, // js date's getMonth returns 0 indexed months
			day: date.getDate()
		}, { zone: "utc" }) as DateTime
	} else {
		const newDateTime = DateTime.fromISO(date).toUTC()
		dateTime = (DateTime as any).fromObject({
			year: newDateTime.year,
			month: newDateTime.month,
			day: newDateTime.day
		}, { zone: "utc" }) as DateTime
	}

	return dateTime.toJSDate()
}

/**
 * Changes dateTime to the START of the day of your local dateTime
 * - E.g 2022-09-03T05:00:00Z with a local timezone of GMT will become 2022-09-02T23:00:00.000Z
 * - E.g 2022-09-03T05:00:00Z with a local timezone of PST will become 2022-09-03T07:00:00.000Z
 * @param {Date | string} date The DateTime object or string to convert.
 * @returns {Date} The Date representing the start of the day in the local timezone.
 */
export const asLocal: (date: Date | string) => Date = (date) => {
	let dateTime: DateTime | null = null
	if (date instanceof Date) {
		dateTime = (DateTime as any).fromObject({
			year: date.getUTCFullYear(),
			month: date.getUTCMonth() + 1, // js date's getMonth returns 0 indexed months
			day: date.getUTCDate()
		}) as DateTime
	} else {
		const utcDateTime = DateTime.fromISO(date)
		dateTime = (DateTime as any).fromObject({
			year: utcDateTime.year,
			month: utcDateTime.month,
			day: utcDateTime.day
		}) as DateTime
	}

	return dateTime.toJSDate()
}

export const convertToTimezone = function (date: Date | string, timezone: string) : Date {
	// Get the date in UTC as it was input, as a Luxon DateTime object.
	let valueDate = date instanceof Date
		? DateTime.fromJSDate(date, { zone: "utc" })
		: DateTime.fromISO(date)

	// DateTime set to any because typescript definitions are out of date.
	// Change this when luxon is ready.
	let luxonDate = (DateTime as any).fromObject({
		year: valueDate.year,
		month: valueDate.month,
		day: valueDate.day,
		hour: valueDate.hour,
		minute: valueDate.minute,
		second: valueDate.second
	}, { zone: timezone })

	// Convert the date to the given timezone using Luxon.
	luxonDate = luxonDate.setZone("utc")

	// Transform the date back to JS Date object.
	return luxonDate.toJSDate()
}

export const applyTimezone = (insight: Insight) => {
	const timezone = insight.defaultTimeZone ??
		Settings.defaultZoneName ??
		(Settings.defaultZone as any)?.zoneName ??
		Intl.DateTimeFormat().resolvedOptions().timeZone

	insight.timeZone = timezone

	applyDefaultTimezoneToClauses(insight.clauses, timezone)
}

export const stripTimeFromDateString: (dateIso: string) => string = (dateIso: string) => {
	const newDateTime = DateTime.fromISO(dateIso).toUTC()
	return ((DateTime as any).fromObject({
		year: newDateTime.year,
		month: newDateTime.month,
		day: newDateTime.day
	}, { zone: "utc" }) as DateTime).toISO()
}

/**
 * Apply default timezone to clauses (insight filters) or dynamic parameter values (dashboard parameters).
 * @param clauses - The clauses or dynamic parameter values to adjust the timezone for.
 * @param timezone - The IANA string of the timezone to convert dates to.
 * @description This function takes the insight clauses and applies the given timezone to the clauses.
 * Used when we need to apply a default timezone setting for insights. Updates the value property, and the
 * searchClauseParameter startDate, endDate, startMonthRange, and endMonthRange to adjust for the default
 * timezone specified, instead of using the user's local machine timezone.
 */
export const applyDefaultTimezoneToClauses = (clauses: Clause[] | DynamicParameterValue[], timezone: string) => {
	if (!clauses || clauses.length === 0) { return }

	if (!timezone) { return }

	clauses.forEach((clause: Clause | DynamicParameterValue) => {
		if (clause.value && isNaN(Number(clause.value)) && !isNaN(Date.parse(clause.value))) {
			var date: Date = new Date(clause.value)	// Ensure it's Date type.
			// Need to convert it to JSON prematurely, because Value is string.
			clause.value = convertToTimezone(date, timezone).toJSON()
		}

		if (clause.searchClauseParameters?.startDate) {
			var startDate: Date = new Date(clause.searchClauseParameters.startDate)
			clause.searchClauseParameters.startDate = convertToTimezone(startDate, timezone)
		}

		if (clause.searchClauseParameters?.endDate) {
			var endDate: Date = new Date(clause.searchClauseParameters.endDate)
			clause.searchClauseParameters.endDate = convertToTimezone(endDate, timezone)
		}

		if (clause.searchClauseParameters?.startMonthRange) {
			var startMonthDate: Date = new Date(clause.searchClauseParameters.startMonthRange)
			clause.searchClauseParameters.startMonthRange = convertToTimezone(startMonthDate, timezone)
		}

		if (clause.searchClauseParameters?.endMonthRange) {
			var endMonthDate: Date = new Date(clause.searchClauseParameters.endMonthRange)
			clause.searchClauseParameters.endMonthRange = convertToTimezone(endMonthDate, timezone)
		}
	})
}

export default applyDefaultTimezoneToClauses
